// Scenes: the editor's start page (pick a scene, or make one) and the uploader that turns RTAB-Map
// .db files into a scene's scans (server/edit-server.ts PUT …/db/:id, then an "add scan" job:
// prep/scene.py). Also the public site's scene picker (data/scenes.json, written by deploy-cloudview), and the
// editor's page picker in the top bar: all scenes, the scene, or one of its object types (the object editor).

import { proc, type Proc } from './procs.ts';

export const SCENE = /^[a-z0-9][a-z0-9-]{0,39}$/;
export interface SceneInfo { name: string; title: string; scans: number; updated: string; built: boolean; job: { scan: string; state: string; step: string } | null }
export interface Job { id: string; scan: string; state: 'queued' | 'running' | 'done' | 'failed'; step: string; t: string; log: string; result?: { placed?: boolean; how?: string } }

const el = (tag: string, attrs: Record<string, string> = {}, text = '') => {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  if (text) e.textContent = text;
  return e;
};
const W = { 'Content-Type': 'application/json', 'X-Cloudview-Edit': '1' };
const ago = (t: string) => {
  const s = (Date.now() - Date.parse(t)) / 1000;
  return s < 90 ? 'just now' : s < 5400 ? `${Math.round(s / 60)} min ago` : s < 129600 ? `${Math.round(s / 3600)} h ago` : t.slice(0, 10);
};

/** The scene in the URL (?scene=…), if any. */
export function sceneParam(): string | null {
  const q = new URLSearchParams(location.search).get('scene');
  return q && SCENE.test(q) ? q : null;
}

/** The editor's scenes, or null when there is no editor server (the public site). */
export async function listScenes(): Promise<{ scenes: SceneInfo[]; deploy: boolean } | null> {
  try {
    const r = await fetch('api/scenes', { cache: 'no-cache' });
    if (!r.ok) return null;
    const j = await r.json();
    return Array.isArray(j?.scenes) ? j : null;
  } catch { return null; }
}

/** A scan id from a file name: what the server accepts (letters, digits, _ and -). */
export const scanId = (file: string) => file.replace(/\.db$/i, '').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64) || 'scan';

/**
 * Upload .db files to a scene, one at a time with progress, then follow their jobs until each scan
 * is in (or failed). onDone runs when a scan is ready (e.g. reload the scene).
 */
export class Uploader {
  readonly box: HTMLElement;
  private list: HTMLElement;
  private polling = 0;
  private known = new Set<string>();     // jobs this page started or saw running
  private heads = new Map<string, { p: Proc; state: string }>();   // the waiting and running jobs, in the status bar
  constructor(private scene: string, private onDone: () => void, private apiBase = 'api/') {
    this.box = el('div', { class: 'upl' });
    const pick = el('input', { type: 'file', multiple: '', hidden: '' }) as HTMLInputElement;
    const btn = el('button', { class: 'small', title: 'RTAB-Map scans (.db): each becomes a scan of this scene, placed on the others automatically' }, 'Add scans (.db)…');
    btn.onclick = () => pick.click();
    pick.onchange = () => { const fs = [...(pick.files ?? [])]; pick.value = ''; void this.send(fs); };
    this.list = el('div', { class: 'upl-list' });
    this.box.append(btn, pick, this.list);
    void this.poll();
  }

  private row(key: string) {
    let r = this.list.querySelector<HTMLElement>(`[data-k="${CSS.escape(key)}"]`);
    if (!r) {
      r = el('div', { class: 'upl-row', 'data-k': key });
      r.append(el('span', { class: 'upl-name' }), el('progress', { max: '1', value: '0' }), el('span', { class: 'upl-msg mono' }));
      this.list.prepend(r);
    }
    return r;
  }
  private show(key: string, name: string, frac: number | null, msg: string, cls = '') {
    const r = this.row(key);
    r.className = 'upl-row ' + cls;
    r.querySelector('.upl-name')!.textContent = name;
    const p = r.querySelector('progress') as HTMLProgressElement;
    if (frac === null) p.removeAttribute('value'); else p.value = frac;
    p.hidden = cls === 'done' || cls === 'failed';
    r.querySelector('.upl-msg')!.textContent = msg;
  }

  private async send(files: File[]) {
    for (const f of files) {
      const id = scanId(f.name), key = 'up:' + id;
      this.show(key, `${f.name} → ${id}`, 0, `uploading ${(f.size / 1e6).toFixed(0)} MB…`);
      const x = new XMLHttpRequest(), head = proc(`uploading ${f.name}`, { cancel: () => x.abort() });
      try {
        const res = await new Promise<{ status: number; body: { error?: string; job?: Job } }>((ok, fail) => {
          x.open('PUT', `${this.apiBase}scenes/${this.scene}/db/${encodeURIComponent(id)}`);
          x.setRequestHeader('X-Cloudview-Edit', '1');
          x.setRequestHeader('Content-Type', 'application/octet-stream');
          x.upload.onprogress = e => {
            if (!e.lengthComputable) return;
            this.show(key, `${f.name} → ${id}`, e.loaded / e.total, `uploading ${Math.round((100 * e.loaded) / e.total)}% of ${(f.size / 1e6).toFixed(0)} MB`);
            head.set(`${(f.size / 1e6).toFixed(0)} MB`, e.loaded / e.total);
          };
          x.onload = () => { let b = {}; try { b = JSON.parse(x.responseText); } catch { /* not JSON */ } ok({ status: x.status, body: b }); };
          x.onerror = () => fail(new Error('the upload was cut off'));
          x.onabort = () => fail(new Error('upload cancelled'));
          x.send(f);
        }).finally(() => head.end());
        if (res.status !== 201 || !res.body.job) { this.show(key, `${f.name} → ${id}`, null, res.body.error ?? `HTTP ${res.status}`, 'failed'); continue; }
        this.row(key).remove();
        this.known.add(res.body.job.id);
        void this.poll();
      } catch (e) { this.show(key, `${f.name} → ${id}`, null, (e as Error).message, 'failed'); }
    }
  }

  /** A waiting or running job in the status bar; a waiting one can be cancelled (the server drops it and its upload). */
  private head(j: Job) {
    if (this.heads.get(j.id)?.state !== j.state) {   // new, or started (a running one has no Cancel)
      this.unhead(j.id);
      this.heads.set(j.id, { state: j.state, p: j.state === 'queued'
        ? proc(`adding scan ${j.scan}`, { cancel: () => { void this.cancelJob(j.id); } })
        : proc(`adding scan ${j.scan}`, { cantStop: 'it is changing the scene: it runs to the end' }) });
    }
    this.heads.get(j.id)!.p.set(j.state === 'queued' ? 'waiting for the scan before it' : j.step);
  }
  private unhead(id: string) { this.heads.get(id)?.p.end(); this.heads.delete(id); }
  private async cancelJob(id: string) {
    const r = await fetch(`${this.apiBase}scenes/${this.scene}/jobs/${encodeURIComponent(id)}`, { method: 'DELETE', headers: W, body: '{}' }).catch(() => null);
    if (r && !r.ok) { const b = await r.json().catch(() => ({})) as { error?: string }; this.show('job:' + id, id, null, b.error ?? `HTTP ${r.status}`); }
    void this.poll();
  }

  /** Follow the scene's jobs while any is waiting or running. */
  private async poll() {
    if (this.polling) return;
    this.polling = 1;
    try {
      for (;;) {
        const r = await fetch(`${this.apiBase}scenes/${this.scene}/jobs`, { cache: 'no-cache' }).then(r => r.json()).catch(() => null) as { jobs: Job[] } | null;
        const jobs = r?.jobs ?? [];
        let active = false, finished = false;
        for (const j of jobs) {
          const live = j.state === 'queued' || j.state === 'running';
          if (live) this.known.add(j.id);
          if (!this.known.has(j.id) && Date.now() - Date.parse(j.t) > 15 * 60e3) continue;   // older jobs: not this visit's
          this.known.add(j.id);
          active ||= live;
          const key = 'job:' + j.id;
          if (live) this.head(j); else this.unhead(j.id);
          if (j.state === 'queued') this.show(key, j.scan, null, 'waiting for the scan before it…');
          else if (j.state === 'running') this.show(key, j.scan, null, `${j.step}…`);
          else if (j.state === 'failed') this.show(key, j.scan, null, `failed: ${j.step}. ${j.log.trim().split('\n').slice(-2).join(' ')}`, 'failed');
          else {
            const placed = j.result?.placed !== false;
            this.show(key, j.scan, null, placed ? `in the scene: ${j.result?.how ?? 'placed'}` : `in the scene, but ${j.result?.how ?? 'not placed'}: use the Align tool`, placed ? 'done' : 'failed');
            if (!this.row(key).dataset.seen) { this.row(key).dataset.seen = '1'; finished = true; }
          }
        }
        for (const id of [...this.heads.keys()]) if (!jobs.some(j => j.id === id)) this.unhead(id);
        if (finished) this.onDone();
        if (!active) break;
        await new Promise(res => setTimeout(res, 2000));
      }
    } finally { this.polling = 0; }
  }
}

/** The editor's start page: the scenes, a new scene, and for each one opening it or adding scans. */
export function renderSelector(root: HTMLElement, list: { scenes: SceneInfo[] }, focus: string | null = null) {
  root.hidden = false;
  root.textContent = '';
  const page = el('div', { class: 'scn-page' });
  page.append(el('h1', {}, 'Scenes'), el('p', { class: 'help' }, 'Open a scene to view and edit it, or make a new one and add RTAB-Map scans (.db files) to it. Scans are placed on the scene\'s other scans automatically; fix any that miss with the Align tool.'));
  const grid = el('div', { class: 'scn-grid' });
  const reload = async (f: string | null) => { const l = await listScenes(); if (l) renderSelector(root, l, f); };
  for (const s of list.scenes) {
    const card = el('div', { class: 'scn-card' + (s.name === focus ? ' on' : '') });
    const open = el('a', { class: 'scn-open', href: `?scene=${s.name}` });
    open.append(el('b', {}, s.title || s.name), el('span', { class: 'help' }, `${s.name} · ${s.scans} scan${s.scans === 1 ? '' : 's'} · ${ago(s.updated)}`));
    if (!s.built) open.removeAttribute('href');
    card.append(open);
    if (s.job) card.append(el('div', { class: 'mono' }, `${s.job.state === 'queued' ? 'waiting' : s.job.step}: ${s.job.scan}`));
    if (!s.built || s.name === focus) {
      const up = new Uploader(s.name, () => { void reload(s.name); });
      card.append(up.box);
    } else {
      const add = el('button', { class: 'small' }, 'Add scans…');
      add.onclick = () => renderSelector(root, list, s.name);
      card.append(add);
    }
    grid.append(card);
  }
  // a new scene
  const nw = el('div', { class: 'scn-card scn-new' }), title = el('input', { type: 'text', placeholder: 'Title, e.g. St Mary, Wells', maxlength: '80' }) as HTMLInputElement;
  const name = el('input', { type: 'text', placeholder: 'name in links, e.g. st-mary-wells', maxlength: '40', autocapitalize: 'none', autocorrect: 'off', spellcheck: 'false' }) as HTMLInputElement;
  const make = el('button', {}, 'New scene'), msg = el('div', { class: 'mono' });
  let edited = false;
  name.oninput = () => { edited = true; };
  title.oninput = () => { if (!edited) name.value = title.value.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40); };
  make.onclick = async () => {
    const n = name.value.trim();
    if (!SCENE.test(n)) { msg.textContent = 'the name: lower-case letters, digits and -, up to 40'; return; }
    const r = await fetch('api/scenes', { method: 'POST', headers: W, body: JSON.stringify({ name: n, title: title.value.trim() || n }) });
    const b = await r.json().catch(() => ({}));
    if (!r.ok) { msg.textContent = b.error ?? `HTTP ${r.status}`; return; }
    void reload(n);
  };
  nw.append(el('b', {}, 'New scene'), title, name, make, msg);
  grid.append(nw);
  page.append(grid);
  root.append(page);
}

/** The public site's scene picker, when deploy-cloudview published more than one scene. */
export async function publicPicker(host: HTMLElement, current: string) {
  try {
    const r = await fetch('data/scenes.json', { cache: 'no-cache' });
    if (!r.ok) return;
    const list = (await r.json()) as { name: string; title: string }[];
    if (!Array.isArray(list) || list.length < 2) return;
    const sel = el('select', { title: 'scene' }) as HTMLSelectElement;
    for (const s of list) sel.append(el('option', { value: s.name }, s.title || s.name));
    sel.value = current;
    sel.onchange = () => { location.href = `?scene=${encodeURIComponent(sel.value)}`; };
    host.append(sel);
  } catch { /* no list: one scene */ }
}

/** The editor's page, a ?scene=…&type=… URL: the scene list (no scene), the scene, or the object editor on a type. */
export function pageUrl(scene: string | null, type: string | null = null) {
  if (!scene) return './';
  return `./?scene=${encodeURIComponent(scene)}${type ? `&type=${encodeURIComponent(type)}` : ''}`;
}

/** The editor's top bar title as a picker: All scenes, the scene, then its object types with their instance
 *  counts (asked again each time it opens, so new ones show). Picking one goes there, if canLeave lets it. */
export function pagePicker(host: HTMLElement, o: { scene: string; title: string; type: string | null; types: () => [string, number][]; canLeave?: () => boolean }) {
  const sel = el('select', { title: 'the page: every scene, this scene, or the object editor on one of its types' }) as HTMLSelectElement;
  const cur = o.type ? `type:${o.type}` : 'scene';
  const fill = () => {
    const types = o.types();
    if (o.type && !types.some(([t]) => t === o.type)) types.unshift([o.type, 0]);
    sel.textContent = '';
    sel.append(el('option', { value: 'all' }, 'All scenes'), el('option', { value: 'scene' }, o.title));
    if (types.length) {
      const g = el('optgroup', { label: 'Objects' });
      for (const [t, n] of types) g.append(el('option', { value: `type:${t}` }, `${t} (${n})`));
      sel.append(g);
    }
    sel.value = cur;
  };
  fill();
  sel.addEventListener('pointerdown', fill);
  sel.addEventListener('focus', fill);
  sel.onchange = () => {
    const v = sel.value;
    if (v === cur) return;
    if (o.canLeave && !o.canLeave()) { sel.value = cur; return; }
    location.href = v === 'all' ? pageUrl(null) : pageUrl(o.scene, v === 'scene' ? null : v.slice(5));
  };
  host.textContent = '';
  host.append(sel);
}
